import React, { useState, useEffect } from "react";
import {
  Stack,
  Button,
  Group,
  Paper,
  Text,
  Alert,
  Divider,
  LoadingOverlay,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useProducts } from "../../../../../context/ProductsContext";
import { useSalon } from "../../../../../context/SalonContext";
import {
  Product as TransactionProduct,
  EditTransactionLineItemsRequest,
} from "../../../../../types/transactions";
import { Employee } from "../../../../../types/salon";
import ProductLookup from "./ProductLookup";
import { useResponsive } from "../../../../../context/ResponsiveContext";
import ProductDetailCard from "../../../../common/ProductDetailCard";
import { getDefaultCommissionTarget } from "../../../../../utils/commissionUtils";

interface ProductFormProps {
  mode: "add" | "edit";
  transaction: any;
  product?: TransactionProduct; // Only required for edit mode
  onSubmit: (data: any) => void;
  onCancel: () => void;
}

export default function ProductForm({
  mode,
  transaction,
  product,
  onSubmit,
  onCancel,
}: ProductFormProps) {
  const { isMobile, isTablet } = useResponsive();
  const { getProductById, setStatusFilter } = useProducts();
  const { currentUser } = useSalon() as {
    currentUser: Employee | null;
  };
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(
    mode === "edit" && product ? product.id : "",
  );
  const [quantity, setQuantity] = useState(
    mode === "edit" && product ? product.quantity : 1,
  );

  // Initialize the commission target. For a NEW product the default is the
  // first booked service's staff (the appointment's provider), else the
  // logged-in seller; an edit carries the stored target through unchanged. The
  // target is reassigned from the row-level picker in the transaction view
  // (ProductCommissionLine), not from this form.
  const [staffCommissionTarget] = useState<string | null | undefined>(() => {
    if (mode === "edit" && product) {
      return product.staff_commission_target;
    }
    return getDefaultCommissionTarget(
      transaction?.line_items?.booked_services,
      currentUser?.id,
    );
  });

  useEffect(() => {
    // Only enable active products for the product lookup
    setStatusFilter("Active");
  }, []);

  const selectedProduct = getProductById(selectedProductId);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!selectedProductId) {
      newErrors.product = "Product is required";
    }
    if (quantity <= 0) {
      newErrors.quantity = "Quantity must be greater than 0";
    }
    if (selectedProduct && quantity > selectedProduct.quantity) {
      newErrors.quantity = `Only ${selectedProduct.quantity} items available in stock`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      if (mode === "add") {
        const lineItemData = {
          products: [
            {
              product_uuid: selectedProductId,
              quantity: quantity,
              ...(staffCommissionTarget !== undefined && {
                staff_commission_target: staffCommissionTarget,
              }),
            },
          ],
        };

        onSubmit(lineItemData);
      } else {
        // Edit mode
        const lineItemData: EditTransactionLineItemsRequest = {
          transaction_id: transaction.transaction.id,
          line_items: {
            products: [
              {
                id: selectedProductId,
                line_item_id: product!.line_item_id,
                quantity: quantity,
                ...(staffCommissionTarget !== undefined && {
                  staff_commission_target: staffCommissionTarget,
                }),
              },
            ],
          },
        };

        onSubmit(lineItemData);
      }
    } catch (error) {
      setErrors({
        general:
          mode === "add"
            ? "Failed to add product. Please try again."
            : "Failed to update product. Please try again.",
      });
      setIsSubmitting(false);
    }
  };

  const handleProductChange = (productId: string) => {
    setSelectedProductId(productId);
    setErrors((prev) => ({ ...prev, product: "" }));
    // Reset quantity to 1 when product changes
    setQuantity(1);
  };

  const handleQuantityChange = (value: number | string) => {
    const numValue = typeof value === "string" ? parseFloat(value) || 1 : value;
    setQuantity(Math.max(1, numValue));
    setErrors((prev) => ({ ...prev, quantity: "" }));
  };

  return (
    <Paper p="md">
      <LoadingOverlay visible={isSubmitting} />
      <Stack gap="lg">
        {errors.general && (
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
            {errors.general}
          </Alert>
        )}

        <ProductLookup
          value={selectedProductId}
          onChange={handleProductChange}
          error={errors.product}
        />

        {selectedProduct && (
          <ProductDetailCard
            product={selectedProduct}
            quantity={quantity}
            onQuantityChange={handleQuantityChange}
            error={errors.quantity}
          />
        )}

        <Divider />

        {selectedProduct && (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Total Price:
            </Text>
            <Text size="lg" fw={500}>
              ${(selectedProduct.list_price * quantity).toFixed(2)}
            </Text>
          </Group>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={!selectedProductId}
          >
            {mode === "add" ? "Add Product" : "Update Product"}
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}
